import { useQuery } from '@tanstack/react-query';

/**
 * useSwaggerAvailable — queue #645 §UAT-2026-07-07-R645 §R645-2.
 *
 * Swagger UI 딥링크 버튼은 **런타임 가용성 탐지** 후에만 노출한다. `/v3/api-docs` 를 origin
 * 기준(★ apiClient 의 `/api/eraf` baseURL 이 아님 — swagger-ui/v3 경로는 prefix 밖) 1회
 * 프로브해 **2xx + JSON 응답**이면 가용, 그 외(404/403/네트워크 오류/HTML)면 부재로 판정한다.
 * 결과는 React Query `staleTime: Infinity` 로 모듈 스코프 캐시(재프로브 없음).
 *
 * ★ content-type 검사 필수: biz-api 는 springdoc 미탑재라 `/v3/api-docs` 가 nginx SPA
 * catch-all 로 `200 text/html`(index.html) 을 반환한다. `res.ok` 만 보면 SPA HTML 을
 * OpenAPI 문서로 오인해 dead Swagger 링크를 노출하므로, 실제 OpenAPI JSON(application/json)
 * 인지까지 확인해야 한다. 현재 prod 는 HTML fallback → 버튼 전역 숨김(dead-link 0).
 */

/** VITE_API_BASE(기본 "/api/eraf")가 절대 URL 이면 그 origin, 상대경로면 현재 페이지 origin. */
export function apiOrigin(): string {
  const apiBase = import.meta.env.VITE_API_BASE ?? '/api/eraf';
  try {
    return new URL(apiBase, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

/** Swagger UI 홈 딥링크(operation 앵커 없음 — §R645-2 근거로 홈 링크로 확정). */
export function swaggerUiUrl(): string {
  return `${apiOrigin()}/swagger-ui/index.html`;
}

async function probeSwaggerAvailable(): Promise<boolean> {
  try {
    const res = await fetch(`${apiOrigin()}/v3/api-docs`, { method: 'GET' });
    // 2xx + JSON 만 가용. springdoc 미탑재 시 nginx SPA catch-all 이 200 text/html 를
    // 돌려주므로 content-type 검사로 SPA HTML fallback 을 걸러낸다(dead-link 방지).
    if (!res.ok) return false;
    const contentType = res.headers.get('content-type') ?? '';
    return contentType.includes('application/json');
  } catch {
    return false; // 네트워크 오류 등도 부재로 취급(안전 기본값)
  }
}

export function useSwaggerAvailable() {
  return useQuery<boolean>({
    queryKey: ['common-showcase', 'swagger-available'],
    queryFn: probeSwaggerAvailable,
    staleTime: Infinity,
    retry: false,
  });
}
